import React from "react";
import {
  Typography,
  Box,
  Divider,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Healing as HealingIcon,
  Assignment as AssignmentIcon,
  Favorite as FavoriteIcon,
  LocalHospital as LocalHospitalIcon,
  TrackChanges as TrackChangesIcon
} from "@mui/icons-material";
import reportData from "./emrBreastCancerReport.json";

const ReportViewer: React.FC = () => {
  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: "100%", margin: "0 auto" }}>
      {/* Header Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }} variant="outlined">
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
          <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
            {reportData.emr.metadata.report_type}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {reportData.emr.metadata.facility_name}
          </Typography>
          <Chip
            label={`Report ID: ${reportData.emr.metadata.report_id}`}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            Report Date: {new Date(reportData.emr.metadata.report_date).toLocaleDateString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last Updated: {new Date(reportData.emr.metadata.last_updated).toLocaleDateString()}
          </Typography>
        </Box>
      </Paper>

      {/* Patient Demographics */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Patient Demographics</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }} >
              <Typography variant="subtitle2" color="text.secondary">Patient ID</Typography>
              <Typography variant="body1" gutterBottom>{reportData.emr.patient.demographics.patient_id}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Name</Typography>
              <Typography variant="body1" gutterBottom>
                {reportData.emr.patient.demographics.first_name} {reportData.emr.patient.demographics.last_name}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(reportData.emr.patient.demographics.date_of_birth).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
              <Typography variant="body1" gutterBottom>{reportData.emr.patient.demographics.gender}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Race</Typography>
              <Typography variant="body1" gutterBottom>{reportData.emr.patient.demographics.race}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Ethnicity</Typography>
              <Typography variant="body1" gutterBottom>{reportData.emr.patient.demographics.ethnicity}</Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Risk Factors */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FavoriteIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Risk Factors</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Family History</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {reportData.emr.patient.breast_cancer_risk_factors.family_history.mother ?
                      <Chip label="Yes" color="primary" size="small" /> :
                      <Chip label="No" variant="outlined" size="small" />}
                  </ListItemIcon>
                  <ListItemText primary="Mother" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {reportData.emr.patient.breast_cancer_risk_factors.family_history.sister ?
                      <Chip label="Yes" color="primary" size="small" /> :
                      <Chip label="No" variant="outlined" size="small" />}
                  </ListItemIcon>
                  <ListItemText primary="Sister" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {reportData.emr.patient.breast_cancer_risk_factors.family_history.aunt ?
                      <Chip label="Yes" color="primary" size="small" /> :
                      <Chip label="No" variant="outlined" size="small" />}
                  </ListItemIcon>
                  <ListItemText primary="Aunt" />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`BRCA Mutation: ${reportData.emr.patient.breast_cancer_risk_factors.family_history.brca_mutation}`}
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Reproductive History</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary={`Menarche Age: ${reportData.emr.patient.breast_cancer_risk_factors.reproductive_history.menarche_age}`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={`Menopause Status: ${reportData.emr.patient.breast_cancer_risk_factors.reproductive_history.menopause_status}`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={`First Pregnancy Age: ${reportData.emr.patient.breast_cancer_risk_factors.reproductive_history.first_pregnancy_age}`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={`Parity: ${reportData.emr.patient.breast_cancer_risk_factors.reproductive_history.parity}`} />
                </ListItem>
              </List>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Lifestyle Factors</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary={`Alcohol Use: ${reportData.emr.patient.breast_cancer_risk_factors.lifestyle_factors.alcohol_use}`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={`BMI: ${reportData.emr.patient.breast_cancer_risk_factors.lifestyle_factors.bmi}`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={`Physical Activity: ${reportData.emr.patient.breast_cancer_risk_factors.lifestyle_factors.physical_activity}`} />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Clinical Findings */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <HealingIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Clinical Findings</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Presentation</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Symptom Onset:</strong> {new Date(reportData.emr.clinical_findings.presentation.date_of_symptom_onset).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Diagnosis Date:</strong> {new Date(reportData.emr.clinical_findings.presentation.date_of_diagnosis).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2"><strong>Initial Symptoms:</strong> {reportData.emr.clinical_findings.presentation.initial_symptoms.palpable_lump ? 'Palpable lump' : 'No lump'}</Typography>
                    {reportData.emr.clinical_findings.presentation.initial_symptoms.palpable_lump && (
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Location: {reportData.emr.clinical_findings.presentation.initial_symptoms.location}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Imaging</Typography>
              <Grid container spacing={2}>
                {reportData.emr.clinical_findings.diagnostic_workup.imaging.map((image, index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 6 }} key={index}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AssignmentIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2">{image.type}</Typography>
                      </Box>
                      <Typography variant="body2"><strong>Date:</strong> {new Date(image.date).toLocaleDateString()}</Typography>
                      {image.birads && (
                        <Typography variant="body2"><strong>BIRADS:</strong> {image.birads}</Typography>
                      )}
                      <Typography variant="body2"><strong>Findings:</strong> {image.findings}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Biopsy Results</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Type:</strong> {reportData.emr.clinical_findings.diagnostic_workup.biopsy.type}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Date:</strong> {new Date(reportData.emr.clinical_findings.diagnostic_workup.biopsy.date).toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Typography variant="body2"><strong>Location:</strong> {reportData.emr.clinical_findings.diagnostic_workup.biopsy.location}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Histology:</strong> {reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.histology}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Grade:</strong> {reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.grade}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2">
                      <strong>ER Status:</strong>
                      <Chip
                        label={reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.er_status}
                        size="small"
                        color={reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.er_status.includes('Positive') ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2">
                      <strong>PR Status:</strong>
                      <Chip
                        label={reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.pr_status}
                        size="small"
                        color={reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.pr_status.includes('Positive') ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2">
                      <strong>HER2 Status:</strong>
                      <Chip
                        label={reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.her2_status}
                        size="small"
                        color={reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.her2_status.includes('Positive') ? 'primary' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Ki67:</strong> {reportData.emr.clinical_findings.diagnostic_workup.biopsy.results.ki67}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Staging</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>CT Chest/Abdomen/Pelvis:</strong> {reportData.emr.clinical_findings.diagnostic_workup.staging_workup.ct_chest_abdomen_pelvis}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Bone Scan:</strong> {reportData.emr.clinical_findings.diagnostic_workup.staging_workup.bone_scan}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Clinical Stage:</strong>
                      <Chip label={reportData.emr.clinical_findings.diagnostic_workup.staging_workup.clinical_stage} color="secondary" sx={{ ml: 1 }} />
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2"><strong>Pathologic Stage:</strong> {reportData.emr.clinical_findings.diagnostic_workup.staging_workup.pathologic_stage}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Treatment Plan */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LocalHospitalIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Treatment Plan</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Multidisciplinary Recommendation</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body1">{reportData.emr.treatment_plan.multidisciplinary_recommendation}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Surgical Plan</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Planned Procedure:</strong> {reportData.emr.treatment_plan.surgical_plan.planned_procedure}</Typography>
                <Typography variant="body2"><strong>Target Date:</strong> {new Date(reportData.emr.treatment_plan.surgical_plan.target_date).toLocaleDateString()}</Typography>
                <Typography variant="body2"><strong>Anticipated Margin Goal:</strong> {reportData.emr.treatment_plan.surgical_plan.anticipated_margin_goal}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Medical Oncology</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Regimen:</strong> {reportData.emr.treatment_plan.medical_oncology.regimen}</Typography>
                <Typography variant="body2"><strong>Cycles Planned:</strong> {reportData.emr.treatment_plan.medical_oncology.cycles_planned}</Typography>
                <Typography variant="body2"><strong>Start Date:</strong> {new Date(reportData.emr.treatment_plan.medical_oncology.start_date).toLocaleDateString()}</Typography>
                <Typography variant="body2"><strong>Growth Factor Support:</strong> {reportData.emr.treatment_plan.medical_oncology.growth_factor_support}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Radiation Oncology</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2">
                  <strong>Planned:</strong>
                  <Chip
                    label={reportData.emr.treatment_plan.radiation_oncology.planned ? 'Yes' : 'No'}
                    size="small"
                    color={reportData.emr.treatment_plan.radiation_oncology.planned ? 'primary' : 'default'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {reportData.emr.treatment_plan.radiation_oncology.planned && (
                  <>
                    <Typography variant="body2"><strong>Anticipated Start:</strong> {new Date(reportData.emr.treatment_plan.radiation_oncology.anticipated_start).toLocaleDateString()}</Typography>
                    <Typography variant="body2"><strong>Technique:</strong> {reportData.emr.treatment_plan.radiation_oncology.technique}</Typography>
                    <Typography variant="body2"><strong>Dose:</strong> {reportData.emr.treatment_plan.radiation_oncology.dose}</Typography>
                  </>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Endocrine Therapy</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2">
                  <strong>Planned:</strong>
                  <Chip
                    label={reportData.emr.treatment_plan.endocrine_therapy.planned ? 'Yes' : 'No'}
                    size="small"
                    color={reportData.emr.treatment_plan.endocrine_therapy.planned ? 'primary' : 'default'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {reportData.emr.treatment_plan.endocrine_therapy.planned && (
                  <>
                    <Typography variant="body2"><strong>Agent:</strong> {reportData.emr.treatment_plan.endocrine_therapy.agent}</Typography>
                    <Typography variant="body2"><strong>Duration:</strong> {reportData.emr.treatment_plan.endocrine_therapy.duration}</Typography>
                    <Typography variant="body2"><strong>Start Timing:</strong> {reportData.emr.treatment_plan.endocrine_therapy.start_timing}</Typography>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Follow-up Plan */}
      <Accordion sx={{
        mb: 2, borderRadius: 2, '&:before': {
          display: 'none',
        },
      }} elevation={2} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TrackChangesIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">Follow-up Plan</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Surveillance Plan</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2"><strong>Physical Exam Frequency:</strong> {reportData.emr.follow_up.surveillance_plan.physical_exam_frequency}</Typography>
                <Typography variant="body2"><strong>Mammogram Frequency:</strong> {reportData.emr.follow_up.surveillance_plan.mammogram_frequency}</Typography>
                <Typography variant="body2"><strong>Other Imaging:</strong> {reportData.emr.follow_up.surveillance_plan.other_imaging}</Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Genetic Counseling</Typography>
              <Paper variant="outlined" sx={{ p: 2, height: 'auto' }}>
                <Typography variant="body2">
                  <strong>Recommended:</strong>
                  <Chip
                    label={reportData.emr.follow_up.genetic_counseling.recommended ? 'Yes' : 'No'}
                    size="small"
                    color={reportData.emr.follow_up.genetic_counseling.recommended ? 'primary' : 'default'}
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {reportData.emr.follow_up.genetic_counseling.recommended && (
                  <Typography variant="body2"><strong>Scheduled:</strong> {new Date(reportData.emr.follow_up.genetic_counseling.scheduled).toLocaleDateString()}</Typography>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">Supportive Care</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2"><strong>Lymphedema Prevention:</strong> {reportData.emr.follow_up.supportive_care.lymphedema_prevention}</Typography>
                <Typography variant="body2"><strong>Fertility Preservation:</strong> {reportData.emr.follow_up.supportive_care.fertility_preservation}</Typography>
                <Typography variant="body2"><strong>Psychosocial Support:</strong> {reportData.emr.follow_up.supportive_care.psychosocial_support}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ReportViewer;